import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { io } from "socket.io-client";
import { Box, Container, Typography, IconButton, TextField, CircularProgress, Stack, Skeleton } from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSnackbar } from "notistack";
import DashboardHeaderSection from "../common/dashboardheader/DashboardHeader";
import GetCookieByName from "../../hooks/GetCookie";
import {
  usePlanSummary,
  usePlanEmailConversations,
  usePlanEmailThread,
  useSendPlanEmailReply,
  useMarkPlanEmailRead,
  useSetPlanEmailAiPaused,
} from "../../hooks/usePlanSummary";
import { isMutationPending } from "./mutationState";
import {
  formatRelativeTime,
  groupMessagesByDate,
  getNumberColor,
  MessageBubble,
  ChatListTabs,
  ChatSearchField,
  UnreadBadge,
  AiStatusChip,
  AiProgressBar,
  sortConversationsNewestFirst,
  filterConversationsByTab,
  filterConversationsBySearch,
  setConversationUnread,
} from "../whatsapp/chatUiHelpers";

// The "View Emails" destination — every email thread belonging to an influencer on THIS
// plan, in the same conversation-list + thread layout as PlanWhatsAppPage.jsx. Reuses
// chatUiHelpers.jsx's generic pieces (date grouping, relative time, the message bubble,
// the deterministic accent color) rather than duplicating them a third time — only the
// WhatsApp-specific bits there (opt-out, wa_id formatting, delivery ticks) don't apply
// here and are simply not used.

function displayName(conv) {
  return conv?.rapidInfluencerId?.userName || conv?.rapidInfluencerId?.username || conv?.influencerEmail || "Unknown";
}

// Adapts an EmailConversation message ({bodyText, direction, isFromUs, sentAt}) into the
// shape MessageBubble already expects ({text, isFromUs, sentAt, type, status}) — type and
// status are simply left undefined (no WhatsApp template/delivery-tick concept in email),
// which MessageBubble already renders correctly as "nothing".
function toBubbleMessage(msg) {
  return { text: msg.bodyText, isFromUs: msg.isFromUs, sentAt: msg.sentAt, isAi: msg.isAi };
}

function ConversationRow({ conv, isSelected, onSelect }) {
  const color = getNumberColor(conv.influencerEmail);
  const lastMsg = (conv.messages || [])[0];
  // Unread comes from the server (the creator's reply is the latest message and nobody opened the thread
  // since); the old "last message is inbound" guess only backs up a backend that doesn't send the flag yet.
  const unread = typeof conv.unread === "boolean" ? conv.unread : Boolean(lastMsg && !lastMsg.isFromUs);
  const unreadMessages = conv.unreadMessages ?? (unread ? 1 : 0);
  const preview = lastMsg ? `${lastMsg.isFromUs ? "You: " : ""}${lastMsg.bodyText || "(no content)"}` : "No messages yet";

  return (
    <Box
      onClick={() => onSelect(conv.conversationId || conv._id)}
      sx={{
        px: 2,
        py: 1.5,
        cursor: "pointer",
        borderLeft: isSelected ? "3px solid #4285F4" : "3px solid transparent",
        bgcolor: isSelected ? "rgba(66,133,244,0.06)" : "transparent",
        borderBottom: "1px solid rgba(0,0,0,0.05)",
        transition: "background-color 0.15s ease",
        "&:hover": { bgcolor: isSelected ? "rgba(66,133,244,0.06)" : "rgba(0,0,0,0.02)" },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
        <Typography
          sx={{
            fontFamily: "Quicksand",
            fontWeight: unread ? 800 : 700,
            fontSize: 13.5,
            color: "#1C1C2E",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {displayName(conv)}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: unread ? "#25D366" : "#9CA3AF", fontWeight: unread ? 700 : 400, flexShrink: 0 }}>
          {formatRelativeTime(conv.lastMessageAt)}
        </Typography>
      </Stack>
      <Typography sx={{ fontFamily: "Quicksand", fontSize: 11, color: "#9CA3AF", pl: 2.25, mt: 0.1 }}>
        {conv.influencerEmail}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 2.25, mt: 0.4 }}>
        <Typography
          sx={{
            fontFamily: "Quicksand",
            fontSize: 12,
            color: unread ? "#374151" : "#9CA3AF",
            fontWeight: unread ? 600 : 400,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          {preview}
        </Typography>
        <AiStatusChip status={conv.qa?.status} paused={conv.qa?.paused} />
        <UnreadBadge count={unreadMessages} />
      </Stack>
    </Box>
  );
}

function ThreadComposer({ onSend, sending }) {
  const [text, setText] = useState("");

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setText("");
    onSend(trimmed);
  };

  return (
    <Box sx={{ borderTop: "1px solid rgba(0,0,0,0.06)", background: "rgba(255,255,255,0.9)", flexShrink: 0 }}>
      <Stack direction="row" alignItems="flex-end" spacing={1} sx={{ px: 2, py: 1.5 }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder="Type a reply…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "18px", fontFamily: "Quicksand", fontSize: 13.5, background: "rgba(0,0,0,0.02)" } }}
        />
        <IconButton
          onClick={handleSend}
          disabled={!text.trim() || sending}
          sx={{
            bgcolor: text.trim() ? "#4285F4" : "rgba(0,0,0,0.06)",
            color: text.trim() ? "#fff" : "#9CA3AF",
            "&:hover": { bgcolor: text.trim() ? "#4285F4" : "rgba(0,0,0,0.06)", opacity: 0.9 },
            width: 38,
            height: 38,
          }}
        >
          {sending ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <SendRoundedIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Stack>
    </Box>
  );
}

function ThreadPanel({ planId, conversationId, onSend, sending }) {
  const { data, isLoading } = usePlanEmailThread(planId, conversationId);
  const { enqueueSnackbar } = useSnackbar();
  const setAiPausedMutation = useSetPlanEmailAiPaused(planId);
  const scrollRef = useRef(null);
  const conversation = data?.conversation;
  const accentColor = getNumberColor(conversation?.influencerEmail);
  const messages = conversation?.messages || [];
  const grouped = groupMessagesByDate(messages);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [conversationId, messages.length]);

  if (!conversationId) {
    return (
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ fontFamily: "Quicksand", color: "#9CA3AF", fontSize: 13.5 }}>
          Select a conversation to view it
        </Typography>
      </Box>
    );
  }

  if (isLoading || !conversation) {
    return (
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5, p: 2.5 }}>
        {[...Array(6)].map((_, i) => (
          <Box key={i} sx={{ display: "flex", justifyContent: i % 2 === 0 ? "flex-end" : "flex-start" }}>
            <Skeleton variant="rounded" width={`${40 + (i % 3) * 15}%`} height={44} sx={{ borderRadius: i % 2 === 0 ? "18px 18px 4px 18px" : "18px 18px 18px 4px" }} />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          background: "rgba(255,255,255,0.85)",
          flexShrink: 0,
        }}
      >
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: accentColor, boxShadow: `0 0 0 3px ${accentColor}30` }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontFamily: "Quicksand", fontWeight: 800, fontSize: 14, color: "#1C1C2E" }}>
            {displayName(conversation)}
          </Typography>
          <Typography sx={{ fontFamily: "Quicksand", fontSize: 10.5, color: accentColor, fontWeight: 700 }}>
            {conversation.influencerEmail}
          </Typography>
        </Box>
      </Box>

      <AiProgressBar
        progress={conversation.aiProgress}
        noun="email"
        pausing={isMutationPending(setAiPausedMutation)}
        onTogglePause={(paused) =>
          setAiPausedMutation.mutate(
            { conversationId, paused },
            { onError: (error) => enqueueSnackbar(error?.response?.data?.message || "Could not change AI pause", { variant: "error" }) }
          )
        }
      />

      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2.5,
          py: 2,
          background: "linear-gradient(145deg, rgba(224,247,232,0.4) 0%, rgba(237,241,255,0.3) 100%)",
        }}
      >
        {Object.entries(grouped).map(([dateLabel, msgs]) => (
          <Box key={dateLabel}>
            <Box sx={{ display: "flex", alignItems: "center", my: 2, gap: 1.5 }}>
              <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(0,0,0,0.06)" }} />
              <Typography sx={{ fontSize: 10, fontWeight: 700, fontFamily: "Quicksand", color: "#9CA3AF", px: 1.5, py: 0.4, bgcolor: "rgba(0,0,0,0.04)", borderRadius: 99, letterSpacing: 0.5 }}>
                {dateLabel}
              </Typography>
              <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(0,0,0,0.06)" }} />
            </Box>
            {msgs.map((msg, i) => (
              <MessageBubble key={msg.messageId || `msg-${dateLabel}-${i}`} message={toBubbleMessage(msg)} accentColor={accentColor} />
            ))}
          </Box>
        ))}
      </Box>

      <ThreadComposer onSend={onSend} sending={sending} />
    </Box>
  );
}

function PlanEmailPage() {
  const { id: planId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const socketRef = useRef(null);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState("all");
  // Threads opened from the Unread tab this visit — kept listed until you leave the tab.
  const [openedIds, setOpenedIds] = useState(() => new Set());
  const [search, setSearch] = useState("");
  // The socket handlers live for the whole visit and can't see fresh state, so they read these.
  // `explicit` is true only once the user has clicked a thread: the page auto-opens the newest thread on
  // load, and that must not silently mark it read before anyone has looked at it.
  const selectedIdRef = useRef(null);
  const explicitSelectRef = useRef(false);
  const markReadRef = useRef(() => {});

  const { data: planData } = usePlanSummary(planId);
  const { data, isLoading } = usePlanEmailConversations(planId);
  const conversations = useMemo(() => sortConversationsNewestFirst(data?.conversations || []), [data]);
  const unreadCount = useMemo(() => conversations.filter((c) => c.unread).length, [conversations]);
  const visibleConversations = useMemo(
    () => filterConversationsBySearch(filterConversationsByTab(conversations, tab, openedIds), search),
    [conversations, tab, openedIds, search]
  );
  const replyMutation = useSendPlanEmailReply(planId);
  const markReadMutation = useMarkPlanEmailRead(planId);

  selectedIdRef.current = selectedConversationId;
  const markRead = (conversationId) => {
    queryClient.setQueryData(["plan-email-conversations", planId], (old) => setConversationUnread(old, conversationId, false));
    markReadMutation.mutate(conversationId);
  };
  markReadRef.current = markRead;

  // Only a click counts as "opened it" — see explicitSelectRef.
  const handleSelect = (conversationId) => {
    explicitSelectRef.current = true;
    setSelectedConversationId(conversationId);
    const conv = conversations.find((c) => c.conversationId === conversationId);
    if (conv?.unread) {
      setOpenedIds((prev) => new Set(prev).add(conversationId));
      markRead(conversationId);
    }
  };

  useEffect(() => {
    if (!selectedConversationId && conversations.length) {
      setSelectedConversationId(conversations[0].conversationId);
    }
  }, [conversations, selectedConversationId]);

  // Live updates — replies are pushed via Gmail's Pub/Sub watch (see
  // pluginbackendnode/routes/gmailWebhook.js), which emits 'email-reply-notify' to the
  // specific staff member's own socket room (already auto-joined on connect — see
  // helpers/socket.js — no per-conversation room join needed here, unlike WhatsApp's
  // shared-number-Inbox model). The notification only carries a snippet, not the full
  // message, so this just invalidates the affected queries rather than trying to
  // reconstruct the message client-side.
  useEffect(() => {
    let authToken;
    try {
      authToken = JSON.parse(GetCookieByName("auth"))["token"];
    } catch (e) {
      return undefined;
    }

    const socket = io(new URL(process.env.REACT_APP_API_URL).origin, { auth: { token: authToken } });
    socketRef.current = socket;

    socket.on("email-reply-notify", ({ conversationId, planId: notifyPlanId }) => {
      if (String(notifyPlanId) !== String(planId)) return;
      queryClient.invalidateQueries({ queryKey: ["plan-email-thread", planId, conversationId] });
      queryClient.invalidateQueries({ queryKey: ["plan-email-conversations", planId] });
      // The creator replied in the thread that is open on screen right now: it is being read.
      if (explicitSelectRef.current && selectedIdRef.current === conversationId) markReadRef.current(conversationId);
    });

    // A teammate opened a thread — refresh so our Unread tab matches. A read from the main Email Inbox
    // carries a plan id too (the thread's own plan), so only this plan's reads refresh the list.
    socket.on("email-read-update", ({ planId: updatedPlanId }) => {
      if (!updatedPlanId || String(updatedPlanId) === String(planId)) {
        queryClient.invalidateQueries({ queryKey: ["plan-email-conversations", planId] });
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [planId, queryClient]);

  const handleSend = async (message) => {
    setSending(true);
    try {
      const res = await replyMutation.mutateAsync({ conversationId: selectedConversationId, message });
      if (!res?.success) throw new Error(res?.message || "Failed to send reply");
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || error.message || "Failed to send reply", { variant: "error" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)" }}>
      <DashboardHeaderSection hideSearch />
      <Container sx={{ marginTop: 8, pb: 6, pl: { xs: 2, sm: 3, lg: "108px" }, pr: { xs: 2, sm: 3 } }} maxWidth="xl">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton onClick={() => navigate(`/plans-summary/${planId}`)} size="small" sx={{ "&:hover": { bgcolor: "rgba(0,0,0,0.04)" } }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography sx={{ fontFamily: "Quicksand", fontWeight: 800, fontSize: 22, display: "flex", alignItems: "center", gap: 1 }}>
              <MailOutlineIcon sx={{ color: "#4285F4" }} /> {planData?.plan?.name ? `${planData.plan.name} — Email` : "Plan Email"}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            border: "1px solid rgba(0,0,0,0.07)",
            borderRadius: "16px",
            overflow: "hidden",
            height: "calc(100vh - 260px)",
            minHeight: 520,
            boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
            bgcolor: "#fff",
          }}
        >
          <Box sx={{ width: { xs: "100%", sm: 300, md: 340 }, flexShrink: 0, borderRight: "1px solid rgba(0,0,0,0.06)", display: "flex", flexDirection: "column" }}>
            <ChatListTabs
              tab={tab}
              onChange={(value) => {
                setTab(value);
                setOpenedIds(new Set()); // leaving the tab drops the threads kept visible after being opened
              }}
              allCount={conversations.length}
              unreadCount={unreadCount}
            />
            <ChatSearchField value={search} onChange={setSearch} placeholder="Search name, email or username" />
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {isLoading ? (
                <Box sx={{ p: 2 }}>
                  {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} variant="rounded" height={54} sx={{ mb: 1, borderRadius: "10px" }} />
                  ))}
                </Box>
              ) : conversations.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography sx={{ fontFamily: "Quicksand", color: "#9CA3AF", fontSize: 13 }}>
                    No emails sent to this plan's influencers yet — use the Send Email button on the
                    plan's influencer list to start one.
                  </Typography>
                </Box>
              ) : visibleConversations.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography sx={{ fontFamily: "Quicksand", color: "#9CA3AF", fontSize: 13 }}>
                    {search.trim() ? `No emails match "${search.trim()}".` : "No unread emails — you're all caught up."}
                  </Typography>
                </Box>
              ) : (
                visibleConversations.map((conv) => (
                  <ConversationRow
                    key={conv._id}
                    conv={conv}
                    isSelected={conv._id === selectedConversationId}
                    onSelect={handleSelect}
                  />
                ))
              )}
            </Box>
          </Box>

          <ThreadPanel planId={planId} conversationId={selectedConversationId} onSend={handleSend} sending={sending} />
        </Box>
      </Container>
    </Box>
  );
}

export default PlanEmailPage;
